import React, { useState } from "react";
import { X, ArrowLeft, ArrowRight } from "lucide-react";

const STEPS = [
  {
    id: "welcome",
    title: "Welcome to UnityLove 💜",
    body: "This is your new home for real connections, flirts, and live lounges. Let us show you around in a few quick steps.",
  },
  {
    id: "dashboard",
    title: "Your Dashboard",
    body: "From your dashboard you can access matches, inbox, flirt center, lounges, and your profile. Think of it as your command center.",
  },
  {
    id: "matches",
    title: "Matches & Messages",
    body: "Check your matches and messages here. This is where 1:1 conversations, flirts, and gifts happen.",
  },
  {
    id: "flirts",
    title: "Flirt Center & Gifts",
    body: "Flirt Center is where you see who's interested in you with flirts and gifts. It's your attraction radar.",
  },
  {
    id: "lounges",
    title: "Live Lounges 🔥",
    body: "Join lounges to vibe live with others through video. Our pairing system connects you 1-on-1 for real-time chemistry.",
  },
  {
    id: "lovecoins",
    title: "LoveCoins Economy",
    body: "LoveCoins let you send gifts, stand out, and get more attention. You can get them through activity, and you can always buy more.",
  },
  {
    id: "done",
    title: "You're All Set!",
    body: "That's it! You can revisit features anytime. Be respectful, have fun, and make meaningful connections.",
  },
];

export default function OnboardingTourModal({ open, onClose }) {
  const [stepIndex, setStepIndex] = useState(0);

  if (!open) return null;

  const step = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  const goNext = () => {
    if (isLast) {
      console.log('[OnboardingTour] Finishing tour, calling onClose');
      onClose();
    } else {
      setStepIndex((prev) => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const goPrev = () => {
    if (!isFirst) {
      setStepIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-gray-900 border border-pink-500/30 shadow-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800">
          <div className="text-xs uppercase tracking-[0.2em] text-pink-400">
            UnityLove Tour
          </div>
          <button
            type="button"
            onClick={handleSkip}
            className="p-1 rounded-full hover:bg-gray-800 text-gray-400 hover:text-gray-100"
            aria-label="Skip tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              {step.title}
            </h2>
            <div className="text-xs text-gray-400">
              Step {stepIndex + 1} of {STEPS.length}
            </div>
          </div>

          <p className="text-sm text-gray-300 leading-relaxed">
            {step.body}
          </p>

          {/* Progress dots */}
          <div className="flex items-center justify-center space-x-1 pt-2">
            {STEPS.map((s, idx) => (
              <div
                key={s.id}
                className={[
                  "h-1.5 rounded-full transition-all",
                  idx === stepIndex
                    ? "w-6 bg-pink-500"
                    : "w-2 bg-gray-700",
                ].join(" ")}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800 bg-gray-950/70 rounded-b-2xl">
          <button
            type="button"
            onClick={handleSkip}
            className="text-xs text-gray-400 hover:text-gray-200"
          >
            Skip tour
          </button>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={goPrev}
              disabled={isFirst}
              className={[
                "inline-flex items-center px-3 py-1 text-xs rounded-full border",
                isFirst
                  ? "border-gray-800 text-gray-600 cursor-not-allowed"
                  : "border-gray-700 text-gray-200 hover:bg-gray-800",
              ].join(" ")}
            >
              <ArrowLeft className="w-3 h-3 mr-1" />
              Back
            </button>
            <button
              type="button"
              onClick={goNext}
              data-testid={isLast ? "tour-finish-btn" : "tour-next-btn"}
              className="inline-flex items-center px-4 py-1.5 text-xs font-medium rounded-full bg-pink-500 hover:bg-pink-400 text-white shadow-md shadow-pink-500/30"
            >
              {isLast ? "Finish" : "Next"}
              {!isLast && <ArrowRight className="w-3 h-3 ml-1" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
